import { AsyncPipe } from '@angular/common';
import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { FnCallPipe } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { AssignedLocationsInfoContainerComponent } from '@core/locations-smart-ui';
import { ViewsOffer, OfferStatus, Priority } from '@core/offers-types';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import type { IconToken } from '@core/user-interface-legacy';
import {
  BadgeComponent,
  CheckboxComponent,
  ClickToCopyDirective,
  DividerComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  RtTooltip,
} from '@core/user-interface-legacy';
import { deepEqual, injectUntilDestroyed } from '@core/utils-legacy';
import type { LayoutBoardCard } from '@core/views-util';
import { LAYOUT_BOARD_SERVICE } from '@core/views-util';

import { JobsDataSourceService } from '../../services/jobs-data-source.service';

type JobsBoardCardField =
  | 'title'
  | 'job_id'
  | 'department'
  | 'location'
  | 'qualified_candidates'
  | 'disqualified_candidates'
  | 'new_candidates'
  | 'work_model'
  | 'hires'
  | 'priority'
  | 'manage';

@Component({
  selector: 'rt-views-offer-board-card',
  templateUrl: './jobs-board-card.component.html',
  imports: [
    DividerComponent,
    HeadingComponent,
    IconComponent,
    TranslatePipe,
    BadgeComponent,
    ClickToCopyDirective,
    RtTooltip,
    OfferStatusIndicatorComponent,
    CheckboxComponent,
    IconButtonComponent,
    AsyncPipe,
    FnCallPipe,
    AssignedLocationsInfoContainerComponent,
  ],
  styleUrls: ['./jobs-board-card.component.less'],
  host: {
    '[class.rt-card]': 'true',
    '[class.selectable-card]': 'true',
    '[class.selected]': 'isSelected',
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
    '(click)': 'onRecordClick()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobsBoardCardComponent implements LayoutBoardCard<ViewsOffer> {
  private readonly jobsDataSourceService = inject(JobsDataSourceService);
  protected readonly OfferStatus = OfferStatus;

  @Input() public record: ViewsOffer;
  @Input() public isSelected = false;
  @Input() public fieldsVisibilityMap: Record<JobsBoardCardField, boolean>;

  @Input() public select = new EventEmitter<ViewsOffer>();

  public newCandidatesLabel: string | null = null;
  public isStatusGrouping = false;
  public isHovered = false;
  public isSelectable = false;
  public locationIds: number[] = [];
  public workModelLabel = '';
  public canManageJobs$ = this.jobsDataSourceService.canManageJobs$;
  public canViewAcquisition$ = inject(Store).select(
    AbilitiesSelectors.hasOne(Ability.ViewAcquisition),
  );
  public jobUrl: string;

  private i18n = inject(I18nService);
  private cdRef = inject(ChangeDetectorRef);
  private layoutBoardDataService = inject(LAYOUT_BOARD_SERVICE);
  private untilDestroyed = injectUntilDestroyed();

  public get hasElementsBeforePriority() {
    return (
      (this.fieldsVisibilityMap.department && this.record.department) ||
      (this.fieldsVisibilityMap.location && this.locationIds.length)
    );
  }

  public get priorityIconColorToken(): IconToken {
    switch (this.record.priority) {
      case Priority.Low:
        return 'colorIconLowDisabled';
      case Priority.Normal:
        return 'colorIconInfoDefault';
      case Priority.High:
        return 'colorIconWarningDefault';
      case Priority.Urgent:
        return 'colorIconCriticalStrongDefault';
      default:
        return 'colorIconDefault';
    }
  }

  public get isInternalOrPublished() {
    return (
      this.record.status === OfferStatus.Internal || this.record.status === OfferStatus.Published
    );
  }

  public ngOnInit() {
    this.layoutBoardDataService.swimlanesData$
      .pipe(
        map(data => [data.groupingId, data.swimlaneGroupingId]),
        distinctUntilChanged(deepEqual),
        this.untilDestroyed,
      )
      .subscribe(([groupingId, swimlaneGroupingId]) => {
        this.isStatusGrouping = groupingId === 'status';
        this.isSelectable = !swimlaneGroupingId;
        this.cdRef.detectChanges();
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.record && this.record) {
      this.newCandidatesLabel = this.record.newCandidatesCount
        ? `${this.record.newCandidatesCount} ${this.i18n.translate('app.common.new')}`
        : null;
      this.locationIds = this.record.locations?.map(location => location.id) ?? [];
      this.workModelLabel = this.getWorkModelLabel();
      this.jobUrl = this.jobsDataSourceService.getOfferUrl(this.record);
    }
  }

  public onMouseEnter() {
    this.isHovered = true;
    this.cdRef.detectChanges();
  }

  public onMouseLeave() {
    this.isHovered = false;
    this.cdRef.detectChanges();
  }

  public isTruncatedFactory = (elRef: HTMLElement) => () => elRef.offsetWidth < elRef.scrollWidth;

  public onRecordClick() {
    this.jobsDataSourceService.onRecordClick(this.record);
  }

  public onFollow(event: MouseEvent) {
    event.stopPropagation();
    this.jobsDataSourceService.toggleOfferFollow(this.record);
  }

  public onEdit(event: MouseEvent) {
    event.stopPropagation();
    this.jobsDataSourceService.onEditClick(this.record);
  }

  public onDuplicate(event: MouseEvent) {
    event.stopPropagation();
    this.jobsDataSourceService.onDuplicate(this.record);
  }

  public onArchive(event: MouseEvent) {
    event.stopPropagation();
    this.jobsDataSourceService.onArchive(this.record);
  }

  public onUnarchive(event: MouseEvent) {
    event.stopPropagation();
    this.jobsDataSourceService.onRetrieve(this.record);
  }

  public onDelete(event: MouseEvent) {
    event.stopPropagation();
    this.jobsDataSourceService.onDelete(this.record);
  }

  public onAcquisition(event: MouseEvent) {
    event.stopPropagation();
    this.jobsDataSourceService.onAcquisition(this.record);
  }

  private getWorkModelLabel(): string {
    const { remote, onSite, hybrid } = this.record;
    return [
      ...(remote ? [this.i18n.translate('app.offer_form.work_model_section.remote.title')] : []),
      ...(onSite ? [this.i18n.translate('app.offer_form.work_model_section.office.title')] : []),
      ...(hybrid ? [this.i18n.translate('app.offer_form.work_model_section.hybrid.title')] : []),
    ].join(', ');
  }
}
